import { ClassValue } from 'clsx';
import { Component, ReactNode, isValidElement, useContext } from 'react';
import { View as RNView, StyleProp, ViewStyle } from 'react-native';
import ArrowsRightIcon from '@shared/assets/icons/arrows/right.svg';
import { MBView, MBViewPadding } from '@shared/components/MBView';
import { Text, TextProps } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBIcon, MBImage, MBImageURISource } from '@shared/components/image';
import { MBSeparator } from '@shared/components/list/separator';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { ColorThemeContext, SharedStyles } from '@shared/styles';
import { Styles } from './styles';

export interface MBListRowProps {
  children?: ReactNode;
  image?: MBImageURISource | ReactNode;
  imageStyle?: StyleProp<ViewStyle>;

  /*
        If set to null, no icon will be shown even if eg there is onPress
    */
  icon?: MBImageURISource | ReactNode;

  title: string | ReactNode;
  subtitle?: string | ReactNode;

  paddingVertical?: MBViewPadding;
  paddingLeft?: MBViewPadding;
  paddingRight?: MBViewPadding;

  onPress?(node: Component): void;

  /*
        Note: could also be a function
    */
  separator?: boolean;

  /*
        If set, the row will have a light gray background. Default is false.
    */
  isSelected?: boolean;

  /*
        If set, this hides the arrow indicator (>) which is shown when
        onPress is set and no icon is set.
    */
  isHidingPressableIndicator?: boolean;

  /*
        If set, this shows a loading indicator where the arrow indicator
        would normally be.
   */
  isLoading?: boolean;

  /*
        Prevent the right icon from being wrapped in a 40x40 view if it is a
        custom component. If we want to add custom content to the right and
        not limit it in terms of size, this prop should be set.
    */
  skipWrappingIcon?: boolean;

  style?: StyleProp<ViewStyle>;
  testID?: string;
  showChildren?: boolean;
  disabled?: boolean;
  className?: ClassValue;
}

export function MBListRow({
  style,
  image,
  imageStyle,
  icon,
  title,
  subtitle,
  onPress,
  separator,
  isHidingPressableIndicator,
  isLoading,
  isSelected,
  disabled,
  skipWrappingIcon,
  paddingVertical = 0.5,
  paddingLeft = 3,
  paddingRight = 1,
  testID,
  showChildren,
  children,
  className,
}: MBListRowProps) {
  const colors = useContext(ColorThemeContext);

  const content = (
    <View testID={testID} fullWidth className={className}>
      {!!isSelected && (
        <RNView
          style={[
            SharedStyles.absoluteCover,
            Styles.highlight,
            { backgroundColor: colors.highlightInactive },
          ]}
        />
      )}
      <MBView
        row
        center
        fullWidth
        paddingVertical={paddingVertical}
        paddingLeft={paddingLeft}
        paddingRight={paddingRight}
        style={{ justifyContent: 'space-between' }}
      >
        <MBListRowLeftContent content={image} style={[Styles.imageMargin, imageStyle]} />

        <View flex>
          <MBListRowText inactive={disabled}>{title}</MBListRowText>
          {subtitle && (
            <MBListRowText inactive style={{ fontSize: 15 }}>
              {subtitle}
            </MBListRowText>
          )}
        </View>

        <MBListRowRightContent
          content={isLoading ? <MenuRowLoadingIndicator /> : icon}
          isArrowIndicatorDefault={!!onPress && !isHidingPressableIndicator}
          skipWrappingIcon={isLoading || !!skipWrappingIcon}
        />
      </MBView>
      {!!separator && <MBSeparator />}
      {showChildren && !!children && <View>{children}</View>}
    </View>
  );

  // Note(fant): wrapping an MBTextInput in MBTouchableHighlight
  // causes issues when clicking space. That makes onPressIn
  // on MBTouchableHighlight be called, instead of adding a space
  // to the MBTextInput. Wrapping an MBListRow in an MBView instead
  // of a disabled MBTouchableHighlight is a workaround to this
  // problem. This is a problem eg in the task notes form field.
  if (!onPress) {
    return <MBView style={[Styles.root, style]}>{content}</MBView>;
  }

  return (
    <MBTouchableOpacity
      onPress={onPress}
      disabled={!onPress || isLoading || disabled}
      style={[
        Styles.root,
        {
          backgroundColor: colors.listRow.background,
        },
        style,
      ]}
    >
      {content}
    </MBTouchableOpacity>
  );
}

interface MBListRowLeftContentProps {
  content: MBImageURISource | ReactNode | undefined;
  style?: StyleProp<ViewStyle>;
}

interface MBListRowRightContentProps {
  content: MBImageURISource | ReactNode | undefined;
  isArrowIndicatorDefault: boolean;
  skipWrappingIcon: boolean;
}

function MBListRowLeftContent({ content, style }: MBListRowLeftContentProps) {
  if (!content) {
    return null;
  }

  // If the content is a custom component, return that.
  if (isValidElement(content)) {
    return <MBView style={style ?? Styles.image}>{content}</MBView>;
  }

  // As default, assume that the content is an image
  // Note(fant): we don't check if what's being given as the image's
  // source is valid. But it can be safely inferred by the object type.
  return <MBImage source={content as MBImageURISource} style={Styles.image} />;
}

function MBListRowRightContent({
  content,
  isArrowIndicatorDefault,
  skipWrappingIcon,
}: MBListRowRightContentProps) {
  const colors = useContext(ColorThemeContext);
  if (!content && !isArrowIndicatorDefault) {
    return <MBView style={Styles.imageMargin} />;
  }

  // If there is no content and we want to show the arrow indicator (>) as default
  // (eg if we have an onPress handler), render that.
  if (!content && isArrowIndicatorDefault) {
    return (
      <MBIcon source={ArrowsRightIcon} style={Styles.image} color={colors.foregroundInactive} />
    );
  }

  // If the content is a custom component and we don't want to wrap it
  if (isValidElement(content) && skipWrappingIcon) {
    return content;
  }

  // If the content is a custom component and we want to wrap it (to limit width)
  if (isValidElement(content) && !skipWrappingIcon) {
    return <MBView style={Styles.image}>{content}</MBView>;
  }

  // As default, assume that the content is an image
  return <MBImage source={content as MBImageURISource} style={Styles.image} />;
}

function MBListRowText({ children, ...otherProps }: TextProps) {
  if (!children) {
    return null;
  }

  // If the content is a custom component, return that.
  if (isValidElement(children)) {
    return children;
  }

  // If the content is text, return that. Note: the content should always
  // be text if it even gets to this point (based on the type if content).
  if (typeof children === 'string') {
    return (
      <Text size="body1" {...otherProps}>
        {children}
      </Text>
    );
  }

  return null;
}

function MenuRowLoadingIndicator() {
  return (
    <View paddingRight={1.5}>
      <MBLoadingIndicator />
    </View>
  );
}
